/**
 * "Something on the phone system changed": said after a change set is applied or undone (by
 * Leland, a review drawer, Change history or an import), so every screen showing that company
 * loads again by itself. Other tabs of the portal hear it too.
 */

const EVENT = 'stitch:changed';

export interface ChangeNotice {
  /** The company it changed for; absent when it could be anything. */
  tenant?: string | undefined;
  /** Only screens following this (useData's `topics`) load again: 'calls' when a call ended. */
  topic?: 'calls' | undefined;
}

let channel: BroadcastChannel | null = null;
function bus(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  try {
    channel = new BroadcastChannel('stitch-changes');
    // Another tab changed something: tell this tab's screens.
    channel.onmessage = (ev: MessageEvent<ChangeNotice>) => window.dispatchEvent(new CustomEvent<ChangeNotice>(EVENT, { detail: ev.data ?? {} }));
  } catch {
    channel = null;
  }
  return channel;
}

/** While something makes many changes in a row (an import's parts), they're said once at the end. */
let holds = 0;
const held = new Map<string, ChangeNotice>();
export function holdChanges(): () => void {
  holds++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds--;
    if (holds > 0) return;
    const all = [...held.values()];
    held.clear();
    for (const n of all) announceChange(n);
  };
}

export function announceChange(notice: ChangeNotice = {}): void {
  if (holds > 0) {
    held.set(notice.tenant ?? '*', notice);
    return;
  }
  window.dispatchEvent(new CustomEvent<ChangeNotice>(EVENT, { detail: notice }));
  try {
    bus()?.postMessage(notice);
  } catch {
    // A closed channel: other tabs catch up when they're next looked at.
  }
}

/** Tells this tab's screens only: each open Stitch heard the news itself (a new voicemail). */
export function announceHere(notice: ChangeNotice = {}): void {
  window.dispatchEvent(new CustomEvent<ChangeNotice>(EVENT, { detail: notice }));
}

export function onChange(listener: (notice: ChangeNotice) => void): () => void {
  bus();
  const handler = (ev: Event) => listener((ev as CustomEvent<ChangeNotice>).detail ?? {});
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

// Someone may have changed things while this tab was in the background (a colleague, or the phone
// system itself): coming back after a minute or more counts as a change.
const AWAY_MS = 60_000;
let hiddenAt = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt >= AWAY_MS) {
      hiddenAt = 0;
      window.dispatchEvent(new CustomEvent<ChangeNotice>(EVENT, { detail: {} }));
    }
  });
}
